import { Box, makeStyles } from '@material-ui/core';
import { navigate, useParams } from '@reach/router';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import PageContent from '../../../components/Layout/PageContent';
import LoadingContainer from '../../../components/LoadingContainer';
import { BenchmarkLocationProps } from '../../../components/TableGrid/Renderers/LinkCellRenderer/BenchmarkLinkCellRenderer';
import { api } from '../../../core/api';
import { FontSizes } from '../../../core/config/mainTheme';
import { toCssValue } from '../../../core/utils/theme.utils';
import { ROUTES } from '../../../routes';
import {
  CompaniesContextProvider,
  useCompaniesDispatch,
} from '../../FinancialData/shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../../FinancialData/shared/CompanyContext/CompanyContext.types';
import { useErrorsDispatch } from '../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkDispatch, useBenchmarkState } from '../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../Context/BenchmarkContext.types';
import BenchmarkTabs from './BenchmarkTabs';
import { TabNamesEnum } from './BenchmarkTabs/BenchmarkTabs';
import ThemeBreadcrumbs from './BreadCrumbs';
import { BreadCrumbLink } from './BreadCrumbs/BreadCrumbs';
import ResetBox from './ResetBox';

const BenchmarkPage: React.FC<BenchmarkLocationProps> = ({ location }) => {
  const classes = useStyles();
  const { benchmarkId, tabName } = useParams<{ benchmarkId: string; tabName?: string }>();
  const { activeBenchmark, activeTab } = useBenchmarkState();
  const dispatch = useBenchmarkDispatch();
  const dispatchCompany = useCompaniesDispatch();
  const dispatchErrors = useErrorsDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const breadCrumbs: BreadCrumbLink[] = [
    {
      path: generatePath(ROUTES.benchmarking.page),
      text: t('app:navTab:benchmarkingData'),
    },
    {
      text: activeBenchmark?.name,
    },
  ];

  const handleResetCompIDAIReport = async () => {
    if (!benchmarkId) return;
    try {
      const resetResult = await api.benchmark.resetBenchwise(benchmarkId);
      if (resetResult) {
        setTimeout(() => {
          const newPath = generatePath(ROUTES.benchmarking.benchmarkTab, {
            benchmarkId: benchmarkId,
            tabName: 'summary',
          });
          navigate(newPath);
        }, 0);
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
    }
  };

  useEffect(() => {
    ReactGA.pageview(window.location.pathname);
    if (benchmarkId) {
      setIsLoading(true);
      // reset only if going to the next benchmark
      if (activeBenchmark && activeBenchmark.id !== benchmarkId) {
        dispatch({
          type: BenchmarkContextActionTypes.ResetBenchmark,
        });
      }
      dispatchCompany({
        type: CompanyContextActionTypes.SetCompany,
        payload: undefined,
      });
      const fetchBenchmarkById = async () => {
        try {
          const data = await api.benchmark.fetchBenchmarkDetails(benchmarkId);
          if (data) {
            dispatch({
              type: BenchmarkContextActionTypes.SetActiveBenchmark,
              payload: data,
            });

            // If a valid tab name is provided in the URL, use it to set the active tab.
            if (tabName) {
              dispatch({
                type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
                payload: tabName,
              });
            } else if (location.state?.switchToRefinementTab) {
              // Do the initial tab switch while loader is spinning. This gives smoother UX
              dispatch({
                type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
                payload: TabNamesEnum.REFINEMENT,
              });
            } else if (
              !location.state?.switchToRefinementTab &&
              activeTab === TabNamesEnum.REFINEMENT
            ) {
              dispatch({
                type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
                payload: TabNamesEnum.SUMMARY,
              });
            }
          }
        } catch (error) {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error },
          });
        } finally {
          setIsLoading(false);
        }
      };
      fetchBenchmarkById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarkId, tabName, dispatch]);

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <Box>
      <div className={classes.breadcrumbContainer}>
        <ThemeBreadcrumbs items={breadCrumbs}>
          <ResetBox onResetCompIDAIReport={handleResetCompIDAIReport} />
        </ThemeBreadcrumbs>
      </div>
      <PageContent>
        <Box className={classes.tabs}>
          <CompaniesContextProvider>
            <BenchmarkTabs />
          </CompaniesContextProvider>
        </Box>
      </PageContent>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  breadcrumbContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.dark,
    display: 'flex',
    flex: 1,
    padding: toCssValue([theme.spacing(0), theme.spacing(6)]),
    '& .MuiTypography-root': {
      fontSize: FontSizes.size.headerSize,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        cursor: 'pointer',
      },
    },
    '& .MuiButtonBase-root': {
      height: '44px',
      boxShadow: 'none',
    },
  },
  tabs: {
    width: 'calc(100%)',
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    //     userSelect: 'none',
  },
}));

export default BenchmarkPage;
